import React, { useEffect, useState } from 'react';
import { BookOpen, Award, Info, X } from 'lucide-react';
import '../certification-pathways.css';

const CertificationPathways = () => {
  const [activePath, setActivePath] = useState(null);

  const pathways = {
    CIM: {
      title: 'CIM (Certificate in Management)',
      subtitle: 'Management Certificate Pathway',
      icon: BookOpen,
      description: 'The CIM pathway helps executives build broad management capability across leadership, strategy, and business decision-making.',
      levels: ['Diploma', 'Advanced Diploma', 'Professional Diploma'],
      keyCompetencies: [
        'Strategic Marketing Planning',
        'Marketing Communications',
        'Digital Marketing Strategy',
        'Customer Experience Management',
        'Data Analytics & Insight',
        'Brand Strategy & Management'
      ],
      duration: '6-12 months',
      assessment: 'Assignments, Case Studies, Examinations',
      prerequisites: 'Bachelor degree or equivalent professional experience',
      programme: '6 months',
      outcomes: [
        'Develop integrated marketing strategy frameworks',
        'Master brand positioning and customer value creation',
        'Lead digital transformation in marketing',
        'Build evidence-based decision-making capabilities',
        'Enhance competitive market analysis skills'
      ]
    },
    CIF: {
      title: 'CIF (Certificate in Finance)',
      subtitle: 'Finance Certificate Pathway',
      icon: Award,
      description: 'The CIF pathway equips business leaders with financial acumen, strategic finance capability, and investment analysis expertise.',
      levels: ['Foundation', 'Intermediate', 'Advanced'],
      keyCompetencies: [
        'Financial Analysis & Reporting',
        'Corporate Finance Strategy',
        'Risk Management & Governance',
        'Investment Analysis',
        'Capital Structure & Valuation',
        'Financial Decision-Making'
      ],
      duration: '8-12 months',
      assessment: 'Practical Projects, Case Analysis, Professional Exams',
      prerequisites: 'Professional experience in finance or related field',
      programme: '9 months',
      outcomes: [
        'Master financial strategy and capital allocation',
        'Develop expert-level corporate finance capabilities',
        'Build valuation and investment appraisal skills',
        'Understand risk frameworks and financial regulation',
        'Enhance board-level financial acumen'
      ]
    }
  };

  const closeModal = () => {
    setActivePath(null);
  };

  useEffect(() => {
    if (!activePath) return undefined;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [activePath]);

  return (
    <section className="pathways-info-section">
      <div className="pathways-grid">
        {Object.entries(pathways).map(([key, pathway]) => {
          const IconComponent = pathway.icon;

          return (
            <div key={key} className="pathway-card">
              <div className="pathway-header">
                <div className="pathway-header-content">
                  <IconComponent size={22} className="pathway-icon" />
                  <h3 className="pathway-title">{pathway.title}</h3>
                </div>
                <button
                  type="button"
                  className="pathway-info-btn"
                  onClick={() => setActivePath(key)}
                  aria-label={`View ${pathway.title} details`}
                  title={pathway.subtitle}
                >
                  <Info size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {activePath && (
        <div className="pathway-modal-overlay" onClick={closeModal} role="presentation">
          <div
            className="pathway-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${pathways[activePath].title} details`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pathway-modal-header">
              <div>
                <h3 className="pathway-modal-title">{pathways[activePath].title}</h3>
                <p className="pathway-modal-subtitle">{pathways[activePath].subtitle}</p>
              </div>
              <button type="button" className="pathway-modal-close" onClick={closeModal} aria-label="Close details">
                <X size={18} />
              </button>
            </div>

            <div className="pathway-details">
              <p className="pathway-description">{pathways[activePath].description}</p>

              <div className="pathway-section">
                <h4 className="pathway-section-title">Programme Duration</h4>
                <p className="pathway-section-content">{pathways[activePath].programme}</p>
              </div>

              <div className="pathway-section">
                <h4 className="pathway-section-title">Certification Levels</h4>
                <ul className="pathway-list">
                  {pathways[activePath].levels.map((level, idx) => (
                    <li key={idx}>{level}</li>
                  ))}
                </ul>
              </div>

              <div className="pathway-section">
                <h4 className="pathway-section-title">Key Competencies</h4>
                <ul className="pathway-list">
                  {pathways[activePath].keyCompetencies.map((competency, idx) => (
                    <li key={idx}>{competency}</li>
                  ))}
                </ul>
              </div>

              <div className="pathway-section">
                <h4 className="pathway-section-title">Learning Outcomes</h4>
                <ul className="pathway-list">
                  {pathways[activePath].outcomes.map((outcome, idx) => (
                    <li key={idx}>{outcome}</li>
                  ))}
                </ul>
              </div>

              <div className="pathway-meta-grid">
                <div className="pathway-meta-item">
                  <span className="pathway-meta-label">Assessment Methods</span>
                  <span className="pathway-meta-value">{pathways[activePath].assessment}</span>
                </div>
                <div className="pathway-meta-item">
                  <span className="pathway-meta-label">Prerequisites</span>
                  <span className="pathway-meta-value">{pathways[activePath].prerequisites}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CertificationPathways;
